import { v4 as uuidv4 } from 'uuid';
import { createSearchParams } from 'react-router-dom';
import {
	IAdministration,
	ICompleteOrder,
	ICheckDomainRes,
	IError,
	IInitialVignetteData,
	IPaymentStatusResponse,
	IRequestBody,
	ITollClass,
	Locales,
} from '../models';
import { supplierMap } from '../constants/supplierMap';
import { OrderService, PayOneService, TollService } from '../services';
import { getDkvOfferHashByModuleAndMembership } from './offerHelpers';
import { getUrlBasePath, shouldCreateInvoice } from './genericHelpers';
import { checkForMaintenance, convertApiDate } from './dateHelpers';
import { getErrorTextFromResponse } from './cartHelpers';
import { formatOrderTrackingData } from '../../../components/src/utils/tracking/tracking';

export const setRouteLangParam = (lang?: string) => `${lang ? `/${lang}` : ''}`;

export const buildApiEndpoint = (path: string) => `${process.env.API_URL || '/api'}${path}`;

export interface IRequestHeaders {
	headers?: { Authorization: string };
}

interface IRequestTrackingHeaders {
	headers: {
		Authorization?: string;
		MAUT_TRACKING_ID: string;
	};
}

export const addTrackingHeader = (config?: IRequestHeaders | undefined): IRequestTrackingHeaders => {
	let sessionId = sessionStorage.getItem('sessionId') ?? '';
	if (!sessionId) {
		sessionId = uuidv4().replace(/-/g, '');
		sessionStorage.setItem('sessionId', sessionId);
	}
	const trackingData = {
		MAUT_TRACKING_ID: sessionId,
	};
	const headers = config?.headers;
	return headers ? { headers: { ...headers, ...trackingData } } : { headers: { ...trackingData } };
};

export const addAuthorizationHeader = (token?: string | null): IRequestHeaders => {
	let config: IRequestHeaders = {};
	if (token) {
		config = {
			headers: { Authorization: token },
		};
	}
	return config;
};

export const getUrlRedirectPath = () => {
	const pathname = window.location.pathname;
	const domain = pathname.includes('fahrzeugwelt')
		? // TODO (CONTENTFUL) needs to be dynamic should come out of contentful later
		  `${window.location.origin}/fahrzeugwelt/maut-vignette/buchung`
		: `${window.location.origin}`;
	return domain;
};

export const languageMap = { de: 'de_DE', en: 'en_EN' };

export const getRequestBody = ({ body, brokerNo, lang = 'de' }: IRequestBody) => {
	const administration: IAdministration = {
		language: languageMap[lang || 'de'],
		operator: 1,
		salesChannel: 9,
		calledFrom: 17,
		sessionId: null,
	};

	if (brokerNo) {
		administration.brokerNo = brokerNo;
	}

	return { administration, ...body };
};

export const checkPayOneStatus = async (
	offerHash: string | null | undefined,
	cartHash: string | null,
	cartToken: string | null,
	millisecondsInterval: number,
	lang?: Locales,
) => {
	let retryCount = 0;
	const retryCountLimit = 30;

	const promise = new Promise<IPaymentStatusResponse | void>((resolve, reject) => {
		const timer = setInterval(() => {
			PayOneService.getPaymentStatus({ offerHash, cartHash }, cartToken, lang).then((result) => {
				if (result.code === 200) {
					sessionStorage.setItem('offerHash', result.offerHash);
					clearInterval(timer);
					resolve(result);
				}
			});
			retryCount++;
			if (retryCount >= retryCountLimit) {
				clearInterval(timer);
				reject();
			}
		}, millisecondsInterval);
	});

	return promise;
};

export const completeOrder = ({
	lang,
	offerHash,
	additionalData,
	variant,
	errorLabel,
	updateOrderInfo,
	onOrderCompleted,
	gatherProductTrackingData,
	setApiError,
	setButtonState = () => {},
}: ICompleteOrder) => {
	const brokerNo = sessionStorage.getItem('branchNumber');
	const customerHash = sessionStorage.getItem('customerHash');
	const cartHash = sessionStorage.getItem('cartHash');
	const token = sessionStorage.getItem('cartToken') || sessionStorage.getItem('customerToken');
	const additionalInfo = sessionStorage.getItem('additionalInfo');

	if (customerHash) {
		if (cartHash && token) {
			const body = {
				cartHash,
				customerHash,
				createInvoice: shouldCreateInvoice(variant, additionalInfo),
			};
			OrderService.completeCartOrder({ body, token, brokerNo, lang }).then((resp) => {
				if (resp.code === 200 && resp.orderInfo) {
					const trackingData = formatOrderTrackingData(resp.orderInfo);
					gatherProductTrackingData(trackingData);
					sessionStorage.setItem('orderInfo', JSON.stringify(resp.orderInfo));
					updateOrderInfo(resp.orderInfo);
					onOrderCompleted(getUrlBasePath(`/order-confirmation${setRouteLangParam(lang)}`));
				} else if (setApiError) {
					const errorText = getErrorTextFromResponse(resp.errors, errorLabel);
					setApiError(errorText);
				}
				if (setButtonState) {
					setButtonState('active');
				}
			});
		} else {
			const body = {
				offerHash,
				customerHash,
				...additionalData,
				createInvoice: shouldCreateInvoice(variant, additionalInfo),
			};
			OrderService.completeOrder(body, token, brokerNo).then((resp) => {
				if (resp.code === 200 && resp.tollReservationInfo) {
					sessionStorage.setItem('orderInfo', JSON.stringify(resp.tollReservationInfo));
					updateOrderInfo(resp.tollReservationInfo);
					onOrderCompleted(getUrlBasePath(`/order-confirmation`));
				} else if (setApiError) {
					const errorText = getErrorTextFromResponse(resp.errors, errorLabel);
					setApiError(errorText);
				}
				if (setButtonState) {
					setButtonState('active');
				}
			});
		}
	}
};

export const fetchInitialVignetteData = async (
	countryId: number,
	{
		navigate,
		lang,
		updateVignetteOffers = () => {},
		updateAllOffers = () => {},
		updateAllCountries = () => {},
		updateAllFuels = () => {},
		updateTollSectionOffers,
		updateIsTransitionPeriod,
	}: IInitialVignetteData,
) => {
	updateVignetteOffers([]);
	updateAllOffers([]);
	await TollService.getAvail({
		body: {
			module: 'TOLL',
			fuelTypeId: -1,
			moduleDetailId: 1,
			moduleTypeId: 1,
			suppliers: supplierMap[countryId],
		},
		lang,
	}).then((data) => {
		const isMaintenaceOngoing = checkForMaintenance(data.offers);
		if (isMaintenaceOngoing && navigate) {
			const params = createSearchParams();
			params.set('type', 'maintenance');
			navigate(getUrlBasePath(`/error${setRouteLangParam(lang)}`, params), { replace: true });
		}
		const today = new Date();
		const vignettes = data.offers.filter(
			({ moduleTypeId, memberNo, sellFrom, sellTo }) =>
				moduleTypeId === 2 &&
				memberNo &&
				convertApiDate(sellFrom).getTime() <= today.getTime() &&
				convertApiDate(sellTo).getTime() >= today.getTime(),
		);
		const currentYear = String(new Date().getFullYear());
		if (vignettes.find((vignette) => !(vignette.servcatSeason === currentYear)) && updateIsTransitionPeriod) {
			updateIsTransitionPeriod(true);
		}
		const tollRoutes = data.offers.filter(
			({ moduleTypeId, memberNo }) => (moduleTypeId === 3 || moduleTypeId === 4) && memberNo,
		);
		updateVignetteOffers(vignettes);
		updateAllOffers(data.offers);
		if (updateTollSectionOffers && tollRoutes.length > 0) {
			updateTollSectionOffers(tollRoutes);
		}
	});

	if (updateAllFuels) {
		await TollService.getFuelTypes().then((data) => {
			const fuels = data.fuelTypes;
			updateAllFuels(fuels);
		});
	}
	await TollService.getCountries(lang).then((data) => {
		const countries = data.countries;
		updateAllCountries(countries);
	});
};

export const fetchInitialDkvData = (updateDkvOffers, updateAllCountries, updateOffer?) => {
	TollService.getAvail({
		body: {
			module: 'TOLL',
			fuelTypeId: -1,
			moduleDetailId: 1,
			moduleTypeId: 1,
			suppliers: ['DKV'],
		},
	}).then((data) => {
		const allDkvOffers = data.offers.filter(({ moduleTypeId }) => moduleTypeId === 1);
		const dkv = data.offers.find(
			({ moduleTypeId, memberNo, fuelTypeId }) => moduleTypeId === 1 && memberNo && fuelTypeId === -1,
		);
		updateDkvOffers(allDkvOffers);
		if (updateOffer) {
			const dkvMemberOfferHash = getDkvOfferHashByModuleAndMembership(allDkvOffers, true);
			if (dkv) {
				sessionStorage.setItem('offerHash', dkv.offerHash);
			}
			sessionStorage.setItem('memberOfferHash', dkvMemberOfferHash);
			updateOffer(dkv);
		}
	});
	TollService.getCountries().then((data) => {
		const countries = data.countries;
		updateAllCountries(countries);
	});
};

export const getFormattedTollClasses = (updateTollClasses) => {
	TollService.getTollClasses().then((data) => {
		const formattedTollClasses = data.tollClasses
			.reduce((acc, tollClass: ITollClass) => {
				const currIndex = acc.findIndex(({ producer }) => producer === tollClass.producer);

				if (currIndex !== -1) {
					const currTollClasss = acc[currIndex];
					currTollClasss.models = [...currTollClasss.models, tollClass.model];
					return acc;
				}
				return [
					...acc,
					{
						id: tollClass.id,
						classCode: tollClass.classCode,
						producer: tollClass.producer,
						models: [tollClass.model],
					},
				];
			}, [])
			.map((e) => ({ ...e, producer: e.producer.toLowerCase() }));
		updateTollClasses(formattedTollClasses);
	});
};

export const getResponseErrorNumber = (errors: IError[]) => {
	const { errorNumber } = errors[0];
	return errorNumber;
};

export const checkEmailDomain = async (email: string): Promise<ICheckDomainRes> => {
	const domain = email.split('@')[1];
	return await TollService.checkDomain({ domain }).then((res) => res);
};
